/**
 * Story 8.12 — Onglet Présentation en lecture seule (modale profil membre)
 * Affiche les informations d'entreprise, description, CA et effectifs.
 * Aucun bouton d'édition. Aucune interaction.
 */
import React from 'react';
import type { ProfileData } from '../../../../../services/profileService';

interface ReadOnlyPresentationProps {
    data: ProfileData;
}

const formatSiret = (siret?: string | null): string => {
    if (!siret) return '-';
    return siret.replace(/(.{3})(.{3})(.{3})(.{5})/, '$1 $2 $3 $4');
};

const formatAdresse = (adresse?: { numero_voie?: string | null; type_voie?: string | null; nom_voie?: string | null; code_postal?: string | null; ville?: string | null; pays?: string | null } | null): string => {
    if (!adresse) return '-';
    const parts = [
        [adresse.numero_voie, adresse.type_voie, adresse.nom_voie].filter(Boolean).join(' '),
        [adresse.code_postal, adresse.ville].filter(Boolean).join(' '),
        adresse.pays,
    ].filter(Boolean);
    return parts.join(', ') || '-';
};

const ReadOnlyPresentation: React.FC<ReadOnlyPresentationProps> = ({ data }) => {
    const { entreprise, presentation } = data;

    return (
        <div className="readonly-tab readonly-presentation">
            {/* Informations légales */}
            <section className="readonly-section">
                <div className="readonly-section-title">Informations légales</div>
                <div className="readonly-grid three-cols">
                    <div className="readonly-field">
                        <label>Nom de l'entreprise</label>
                        <p>{entreprise?.nom || '-'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Précision</label>
                        <p>{entreprise?.precision || '-'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>SIRET</label>
                        <p>{formatSiret(entreprise?.siret)}</p>
                    </div>
                </div>
                <div className="readonly-grid three-cols">
                    <div className="readonly-field">
                        <label>RCS</label>
                        <p>{entreprise?.RCS || '-'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Statut juridique</label>
                        <p>{entreprise?.statut_juridique || '-'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Capital</label>
                        <p>{entreprise?.capital ? `${entreprise.capital.toLocaleString('fr-FR')} €` : '-'}</p>
                    </div>
                </div>
                <div className="readonly-grid three-cols">
                    <div className="readonly-field">
                        <label>Année de création</label>
                        <p>{entreprise?.date_creation || '-'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Redressement judiciaire</label>
                        <p>{entreprise?.redressement_judiciaire ? 'Oui' : 'Non'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Adresse</label>
                        <p>{formatAdresse(entreprise?.adresse)}</p>
                    </div>
                </div>
            </section>

            {/* Description */}
            <section className="readonly-section">
                <div className="readonly-section-title">Description de l'entreprise</div>
                <div className="readonly-field">
                    <label>Présentation de l'entreprise</label>
                    <p className="readonly-text-block">{presentation?.presentation_entreprise || 'Non renseignée'}</p>
                </div>
                <div className="readonly-field">
                    <label>Présentation des moyens humains et matériel</label>
                    <p className="readonly-text-block">{presentation?.presentation_humain || 'Non renseignée'}</p>
                </div>
                <div className="readonly-field">
                    <label>Présentation des modes opératoires</label>
                    <p className="readonly-text-block">{presentation?.presentation_operationnel || 'Non renseignée'}</p>
                </div>
                <div className="readonly-field">
                    <label>Engagements environnementaux et sociaux</label>
                    <p className="readonly-text-block">{presentation?.presentation_environnement || 'Non renseignée'}</p>
                </div>
            </section>

            {/* Capacités économiques */}
            <section className="readonly-section">
                <div className="readonly-section-title">Capacités économiques et financières</div>
                <p className="readonly-hint">Chiffre d'affaires global annuel en € et Part du CA des prestations similaires (%)</p>
                <div className="readonly-grid-pairs">
                    <div className="readonly-pair">
                        <span className="pair-label">(N-1)</span>
                        <span className="pair-value">{presentation?.ca_n1?.toLocaleString('fr-FR') || '-'} €</span>
                        <span className="pair-label">Part</span>
                        <span className="pair-value">{presentation?.part_ca_n1 ?? '-'} %</span>
                    </div>
                    <div className="readonly-pair">
                        <span className="pair-label">(N-2)</span>
                        <span className="pair-value">{presentation?.ca_n2?.toLocaleString('fr-FR') || '-'} €</span>
                        <span className="pair-label">Part</span>
                        <span className="pair-value">{presentation?.part_ca_n2 ?? '-'} %</span>
                    </div>
                    <div className="readonly-pair">
                        <span className="pair-label">(N-3)</span>
                        <span className="pair-value">{presentation?.ca_n3?.toLocaleString('fr-FR') || '-'} €</span>
                        <span className="pair-label">Part</span>
                        <span className="pair-value">{presentation?.part_ca_n3 ?? '-'} %</span>
                    </div>
                </div>
            </section>

            {/* Capacités techniques */}
            <section className="readonly-section">
                <div className="readonly-section-title">Capacités techniques et professionnelles</div>
                <p className="readonly-hint">Effectifs moyens annuels et Part du personnel d'encadrement (%)</p>
                <div className="readonly-grid-pairs">
                    <div className="readonly-pair">
                        <span className="pair-label">(N-1)</span>
                        <span className="pair-value">{presentation?.rh_n1 ?? '-'} ETP</span>
                        <span className="pair-label">Part</span>
                        <span className="pair-value">{presentation?.part_rh_n1 ?? '-'} %</span>
                    </div>
                    <div className="readonly-pair">
                        <span className="pair-label">(N-2)</span>
                        <span className="pair-value">{presentation?.rh_n2 ?? '-'} ETP</span>
                        <span className="pair-label">Part</span>
                        <span className="pair-value">{presentation?.part_rh_n2 ?? '-'} %</span>
                    </div>
                    <div className="readonly-pair">
                        <span className="pair-label">(N-3)</span>
                        <span className="pair-value">{presentation?.rh_n3 ?? '-'} ETP</span>
                        <span className="pair-label">Part</span>
                        <span className="pair-value">{presentation?.part_rh_n3 ?? '-'} %</span>
                    </div>
                </div>
            </section>

            {/* Lots */}
            {data.lots && data.lots.lots_departements && data.lots.lots_departements.length > 0 && (
                <section className="readonly-section">
                    <div className="readonly-section-title">Lots techniques et géographiques</div>
                    <div className="readonly-lots-list">
                        {data.lots.lots_techniques.map((lt) => {
                            const deps = data.lots.lots_departements.filter(ld => ld.lot_technique_id === lt.id);
                            return (
                                <div key={lt.id} className="readonly-lot-group">
                                    <div className="readonly-lot-name">
                                        {lt.code} — {lt.nom}
                                        {lt.is_ac && <span className="readonly-badge-ac">AC</span>}
                                    </div>
                                    <div className="readonly-lot-depts">
                                        {deps.map((ld) => (
                                            <span key={ld.id} className="readonly-dept-tag">
                                                {ld.departement_nom} ({ld.departement_code})
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
};

export default ReadOnlyPresentation;
